import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  User, Plus, Trash2, ChevronDown, ChevronUp, 
  Sparkles, Eye, Palette, Ruler, Heart, Upload, Loader2, Undo2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = '';

// Physical attribute options
const GENDER_OPTIONS = ['Male', 'Female', 'Non-binary', 'Other'];
const BUILD_OPTIONS = ['Slim', 'Average', 'Athletic', 'Muscular', 'Curvy', 'Stocky', 'Heavyset'];
const HAIR_COLORS = ['Black', 'Brown', 'Blonde', 'Red', 'Auburn', 'Gray', 'White', 'Silver', 'Blue', 'Pink', 'Purple', 'Green', 'Bald'];
const HAIR_STYLES = ['Short', 'Medium', 'Long', 'Buzz cut', 'Curly', 'Wavy', 'Straight', 'Braided', 'Dreadlocks', 'Ponytail', 'Bun', 'Mohawk', 'Bald'];
const EYE_COLORS = ['Brown', 'Blue', 'Green', 'Hazel', 'Gray', 'Amber', 'Black', 'Violet', 'Heterochromia'];
const SKIN_TONES = ['Fair', 'Light', 'Medium', 'Olive', 'Tan', 'Brown', 'Dark brown', 'Deep'];
const AGE_RANGES = ['Child (0-12)', 'Teen (13-17)', 'Young Adult (18-25)', 'Adult (26-40)', 'Middle-aged (41-60)', 'Senior (60+)'];

const ROLE_OPTIONS = [
  { value: 'protagonist', label: 'Protagonist', color: 'bg-cyan-600' },
  { value: 'antagonist', label: 'Antagonist', color: 'bg-red-600' },
  { value: 'supporting', label: 'Supporting', color: 'bg-blue-600' },
  { value: 'love_interest', label: 'Love Interest', color: 'bg-pink-600' },
  { value: 'mentor', label: 'Mentor', color: 'bg-purple-600' },
  { value: 'sidekick', label: 'Sidekick', color: 'bg-green-600' },
  { value: 'other', label: 'Other', color: 'bg-gray-600' },
];

// Default character template
const defaultCharacter = {
  name: '',
  role: 'protagonist',
  description: '',
  // Physical attributes
  age: '',
  gender: '',
  height: '',
  build: '',
  hairColor: '',
  hairStyle: '',
  eyeColor: '',
  skinTone: '',
  distinguishingFeatures: '',
  // Personality & Background
  personality: '',
  motivation: '',
  backstory: '',
};

// Single character card component
const CharacterCard = ({ character, index, onChange, onRemove, isExpanded, onToggle, canRemove }) => {
  const roleInfo = ROLE_OPTIONS.find(r => r.value === character.role) || ROLE_OPTIONS[0];
  
  return (
    <Card className="bg-card border border-border">
      <CardHeader className="py-3 px-4 cursor-pointer" onClick={onToggle}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <User className="w-5 h-5 text-cyan-400" />
            <span className="font-semibold text-foreground">
              {character.name || `Character ${index + 1}`}
            </span>
            <Badge className={`${roleInfo.color} text-white text-xs`}>
              {roleInfo.label}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            {canRemove && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-900/30"
                onClick={(e) => { e.stopPropagation(); onRemove(); }}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </div>
      </CardHeader>
      
      {isExpanded && (
        <CardContent className="pt-0 pb-4 px-4 space-y-4">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Name *</Label>
              <Input
                placeholder="Character name"
                value={character.name}
                onChange={(e) => onChange({ ...character, name: e.target.value })}
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Role</Label>
              <select
                value={character.role}
                onChange={(e) => onChange({ ...character, role: e.target.value })}
                className="w-full h-9 rounded-md border border-border bg-background px-3 text-sm"
              >
                {ROLE_OPTIONS.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div>
            <Label className="text-xs text-muted-foreground">Brief Description</Label>
            <Textarea
              placeholder="Who is this character? What's their story?"
              value={character.description}
              onChange={(e) => onChange({ ...character, description: e.target.value })}
              className="min-h-[60px]"
            />
          </div>

          {/* Physical Appearance Section */}
          <div className="border-t border-border pt-3">
            <div className="flex items-center gap-2 mb-3">
              <Palette className="w-4 h-4 text-purple-400" />
              <span className="font-medium text-sm text-foreground">Physical Appearance</span>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Age</Label>
                <select
                  value={character.age}
                  onChange={(e) => onChange({ ...character, age: e.target.value })}
                  className="w-full h-8 rounded-md border border-border bg-background px-2 text-sm"
                >
                  <option value="">Select...</option>
                  {AGE_RANGES.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              
              <div>
                <Label className="text-xs text-muted-foreground">Gender</Label>
                <select
                  value={character.gender}
                  onChange={(e) => onChange({ ...character, gender: e.target.value })}
                  className="w-full h-8 rounded-md border border-border bg-background px-2 text-sm"
                >
                  <option value="">Select...</option>
                  {GENDER_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              
              <div>
                <Label className="text-xs text-muted-foreground">Height</Label>
                <Input
                  placeholder="e.g., 5'10, tall"
                  value={character.height}
                  onChange={(e) => onChange({ ...character, height: e.target.value })}
                  className="h-8 text-sm"
                />
              </div>
              
              <div>
                <Label className="text-xs text-muted-foreground">Build</Label>
                <select
                  value={character.build}
                  onChange={(e) => onChange({ ...character, build: e.target.value })}
                  className="w-full h-8 rounded-md border border-border bg-background px-2 text-sm"
                >
                  <option value="">Select...</option>
                  {BUILD_OPTIONS.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              
              <div>
                <Label className="text-xs text-muted-foreground">Skin Tone</Label>
                <select
                  value={character.skinTone}
                  onChange={(e) => onChange({ ...character, skinTone: e.target.value })}
                  className="w-full h-8 rounded-md border border-border bg-background px-2 text-sm"
                >
                  <option value="">Select...</option>
                  {SKIN_TONES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              
              <div>
                <Label className="text-xs text-muted-foreground">Eye Color</Label>
                <select
                  value={character.eyeColor}
                  onChange={(e) => onChange({ ...character, eyeColor: e.target.value })}
                  className="w-full h-8 rounded-md border border-border bg-background px-2 text-sm"
                >
                  <option value="">Select...</option>
                  {EYE_COLORS.map(e => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>
              
              <div>
                <Label className="text-xs text-muted-foreground">Hair Color</Label>
                <select
                  value={character.hairColor}
                  onChange={(e) => onChange({ ...character, hairColor: e.target.value })}
                  className="w-full h-8 rounded-md border border-border bg-background px-2 text-sm"
                >
                  <option value="">Select...</option>
                  {HAIR_COLORS.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
              
              <div>
                <Label className="text-xs text-muted-foreground">Hair Style</Label>
                <select
                  value={character.hairStyle}
                  onChange={(e) => onChange({ ...character, hairStyle: e.target.value })}
                  className="w-full h-8 rounded-md border border-border bg-background px-2 text-sm"
                >
                  <option value="">Select...</option>
                  {HAIR_STYLES.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
            </div>
            
            <div className="mt-3">
              <Label className="text-xs text-muted-foreground">Distinguishing Features</Label>
              <Input
                placeholder="Scars, tattoos, birthmarks, etc."
                value={character.distinguishingFeatures}
                onChange={(e) => onChange({ ...character, distinguishingFeatures: e.target.value })}
                className="h-8 text-sm"
              />
            </div>
          </div>

          {/* Personality & Background Section */}
          <div className="border-t border-border pt-3">
            <div className="flex items-center gap-2 mb-3">
              <Heart className="w-4 h-4 text-pink-400" />
              <span className="font-medium text-sm text-foreground">Personality & Background</span>
            </div>
            
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-muted-foreground">Personality Traits</Label>
                <Input
                  placeholder="e.g., brave, sarcastic, kind-hearted"
                  value={character.personality}
                  onChange={(e) => onChange({ ...character, personality: e.target.value })}
                  className="h-8 text-sm"
                />
              </div>
              
              <div>
                <Label className="text-xs text-muted-foreground">Motivation/Goal</Label>
                <Input
                  placeholder="What drives this character?"
                  value={character.motivation}
                  onChange={(e) => onChange({ ...character, motivation: e.target.value })}
                  className="h-8 text-sm"
                />
              </div>
              
              <div>
                <Label className="text-xs text-muted-foreground">Backstory (optional)</Label>
                <Textarea
                  placeholder="Key events from their past..."
                  value={character.backstory}
                  onChange={(e) => onChange({ ...character, backstory: e.target.value })}
                  className="min-h-[50px] text-sm"
                />
              </div>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// Main CharacterEditor component
export const CharacterEditor = ({ 
  characters = [{ ...defaultCharacter }], 
  onChange,
  maxCharacters = 30,
  showTitle = true,
  compact = false
}) => {
  const [expandedIndex, setExpandedIndex] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [lastDeleted, setLastDeleted] = useState(null);
  const fileInputRef = useRef(null);
  
  const handleCharacterChange = (index, updatedCharacter) => {
    const newCharacters = [...characters];
    newCharacters[index] = updatedCharacter;
    onChange(newCharacters);
  };
  
  const handleAddCharacter = () => {
    if (characters.length < maxCharacters) {
      const newRole = characters.length === 0 ? 'protagonist' : 
                      characters.length === 1 ? 'antagonist' : 'supporting';
      onChange([...characters, { ...defaultCharacter, role: newRole }]);
      setExpandedIndex(characters.length);
    }
  };
  
  const handleRemoveCharacter = (index) => {
    if (characters.length > 1) {
      const deleted = characters[index];
      const deletedPosition = index;
      setLastDeleted({ character: deleted, position: deletedPosition });
      const newCharacters = characters.filter((_, i) => i !== index);
      onChange(newCharacters);
      if (expandedIndex >= newCharacters.length) {
        setExpandedIndex(Math.max(0, newCharacters.length - 1));
      }
      toast(`Deleted ${deleted.name || 'character'}`, {
        action: {
          label: 'Undo',
          onClick: () => {
            const restored = [...newCharacters];
            restored.splice(deletedPosition, 0, deleted);
            onChange(restored);
            setLastDeleted(null);
            toast.success(`Restored ${deleted.name || 'character'}`);
          },
        },
        duration: 8000,
      });
    }
  };
  
  const handleRestoreCharacter = () => {
    if (!lastDeleted) return;
    const restored = [...characters];
    const pos = Math.min(lastDeleted.position, restored.length);
    restored.splice(pos, 0, lastDeleted.character);
    onChange(restored);
    toast.success(`Restored ${lastDeleted.character.name || 'character'}`);
    setLastDeleted(null);
  };
  
  // Handle character profile file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const validTypes = ['application/pdf', 'text/plain'];
    if (!validTypes.includes(file.type) && !file.name.endsWith('.txt') && !file.name.endsWith('.pdf')) {
      toast.error('Please upload a PDF or TXT file');
      return;
    }
    
    setIsUploading(true);
    toast.info('Parsing character profiles...');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/characters/parse-profiles`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });
      
      if (response.data.success && response.data.characters?.length > 0) {
        // Map parsed characters to our format
        const parsedCharacters = response.data.characters.map(char => ({
          name: char.name || '',
          role: char.role || 'supporting',
          description: char.brief_description || '',
          age: char.age || '',
          gender: char.gender || '',
          height: char.height || '',
          build: char.build || '',
          hairColor: char.hair_color || '',
          hairStyle: char.hair_style || '',
          eyeColor: char.eye_color || '',
          skinTone: char.skin_tone || '',
          distinguishingFeatures: char.distinguishing_features || '',
          personality: char.personality || '',
          motivation: char.motivation || '',
          backstory: char.backstory || ''
        }));
        
        // Replace existing characters with parsed ones (up to max)
        const limitedCharacters = parsedCharacters.slice(0, maxCharacters);
        onChange(limitedCharacters);
        setExpandedIndex(0);
        
        toast.success(`Imported ${limitedCharacters.length} characters!`);
        if (parsedCharacters.length > maxCharacters) {
          toast.info(`Note: Limited to ${maxCharacters} characters (${parsedCharacters.length} found)`);
        }
      } else {
        toast.error('No characters found in the file');
      }
    } catch (error) {
      console.error('Character upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to parse character profiles');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  return (
    <div className="space-y-3">
      {showTitle && (
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <User className="w-5 h-5 text-cyan-400" />
            <span className="font-semibold text-foreground">Characters</span>
            <Badge variant="outline" className="text-xs">
              {characters.length}/{maxCharacters}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            {/* Upload Character Profiles Button */}
            <input
              type="file"
              ref={fileInputRef}
              accept=".pdf,.txt"
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="h-8 text-xs bg-purple-600/20 border-purple-500/50 hover:bg-purple-600/30 text-purple-300"
              data-testid="upload-characters-btn"
            >
              {isUploading ? (
                <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Parsing...</>
              ) : (
                <><Upload className="w-3 h-3 mr-1" /> Upload Profiles</>
              )}
            </Button>
            {characters.length < maxCharacters && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddCharacter}
                className="h-8 text-xs"
              >
                <Plus className="w-3 h-3 mr-1" />
                Add Character
              </Button>
            )}
            {lastDeleted && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRestoreCharacter}
                className="h-8 text-xs bg-amber-600/20 border-amber-500/50 hover:bg-amber-600/30 text-amber-300"
                data-testid="restore-character-btn"
              >
                <Undo2 className="w-3 h-3 mr-1" />
                Restore {lastDeleted.character.name || 'Character'}
              </Button>
            )}
          </div>
        </div>
      )}
      
      <div className="space-y-2">
        {characters.map((character, index) => (
          <CharacterCard
            key={index}
            character={character}
            index={index}
            onChange={(updated) => handleCharacterChange(index, updated)}
            onRemove={() => handleRemoveCharacter(index)}
            isExpanded={expandedIndex === index}
            onToggle={() => setExpandedIndex(expandedIndex === index ? -1 : index)}
            canRemove={characters.length > 1}
          />
        ))}
      </div>
      
      {!showTitle && characters.length < maxCharacters && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleAddCharacter}
          className="w-full h-9"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Another Character
        </Button>
      )}
    </div>
  );
};

export default CharacterEditor;
